'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { spotMarketsBunja } from '@flipster/r230522/original/spot-market';
import { spotTickerBunja } from '@flipster/r230522/original/spot-ticker';
import { getSpotTickerDisplayPrice } from '@flipster/r230522/original/ticker-price';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, grid } from '@flipster/styled-system/patterns';

interface Props {
  symbol: string;
  pricePrecision: number;
  quoteCurrency: string;
}

const SymbolInfo = ({ symbol, pricePrecision, quoteCurrency }: Props) => {
  const { spotMarketsAtom } = useBunja(spotMarketsBunja);
  const market = useAtomValue(spotMarketsAtom);
  const { spotTickersAtom } = useBunja(spotTickerBunja);
  const ticker = useAtomValue(spotTickersAtom);

  const currentMarket = market?.[symbol];
  const currentTicker = ticker?.[symbol];

  return (
    <div
      className={flex({
        p: 16,
        bg: 'background.default.level-3',
        rounded: 8,
        direction: { base: 'column', tablet: 'row' },
        gap: { base: 16, tablet: 32 },
        mb: { base: 16, tablet: 32 },
      })}
    >
      <div className={flex({ flex: 1, direction: 'row', align: 'center', gap: 8 })}>
        <span>
          <Formatted.Number
            method='round-half-up'
            value={getSpotTickerDisplayPrice(currentMarket, currentTicker)}
            precision={pricePrecision}
            toFixed
            css={{ textStyle: 'Title/M' }}
          />
          <span className={css({ pl: 4 })}>{quoteCurrency}</span>
        </span>
        <Formatted.Percent value={currentMarket?.priceChangePct24h} withPlus withColor toFixed />
      </div>
      <hr
        className={css({
          borderBlockEndWidth: undefined,
          borderTopWidth: 0,
          borderLeftWidth: 0,
          borderRightWidth: { base: 0, tablet: 1 },
          borderBottomWidth: { base: 1, tablet: 0 },
          color: 'border.default.level-2',
        })}
      />
      <dl
        className={grid({
          flex: 1,
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 16,
          '& dt': {
            textStyle: 'Text/M/Strong',
            mb: 4,
            whiteSpace: 'nowrap',
          },
        })}
      >
        <div>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_24h_high' />
          </dt>
          <dd>
            <Formatted.Number value={currentTicker?.high24h} precision={pricePrecision} toFixed />
          </dd>
        </div>
        <div>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_24h_low' />
          </dt>
          <dd>
            <Formatted.Number value={currentTicker?.low24h} precision={pricePrecision} toFixed />
          </dd>
        </div>
        <div>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_24h_volume' />
          </dt>
          <dd>
            <Formatted.Number value={currentTicker?.volume24h} precision={pricePrecision} toFixed />
          </dd>
        </div>
        <div>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_24h_turnover' />
          </dt>
          <dd>
            <Formatted.ShortNumber value={currentTicker?.turnover24h} precision={2} toFixed />
          </dd>
        </div>
      </dl>
    </div>
  );
};

export default SymbolInfo;
